import type { CompanySettings, TaxYear, UnionRates, WageAgreement } from './types.js';

/**
 * Statutory and collective-agreement reference data.
 *
 * In production these live in the `tax_years`, `unions`, `wage_agreements` and
 * `holidays` reference tables (see db/migrations) and are served by the API —
 * the values here are the fallback/seed set and the source for the DB seed.
 */

export const HOLIDAYS_2026 = [
  '2026-01-01', '2026-04-02', '2026-04-03', '2026-04-05', '2026-04-06',
  '2026-04-23', '2026-05-01', '2026-05-14', '2026-05-24', '2026-05-25',
  '2026-06-17', '2026-08-03', '2026-12-24', '2026-12-25', '2026-12-26',
  '2026-12-31',
];

/** Withholding constants per tax year (Skatturinn). Not user-adjustable. */
export const TAX_YEARS: Record<number, TaxYear> = {
  2026: { r: [31.49, 37.99, 46.29], t: [498122, 1398450], credit: 72492 },
  2025: { r: [31.48, 37.98, 46.28], t: [472005, 1325127], credit: 68691 },
  2024: { r: [31.48, 37.98, 46.28], t: [446136, 1252501], credit: 64926 },
};

/** Member fee and employer funds (sjúkra / orlofsheimila / endurmenntun) per union. */
export const UNION_RATES: Record<string, UnionRates> = {
  'Efling': { member: 1, employer: 1.55 },
  'VR': { member: 0.7, employer: 1.35 },
  'Eining-Iðja': { member: 1, employer: 1.55 },
  'AFL Starfsgreinafélag': { member: 1, employer: 1.55 },
  'Verkalýðsfélag Akraness': { member: 1, employer: 1.55 },
  'Verkalýðsfélagið Hlíf': { member: 1, employer: 1.55 },
  'Framsýn stéttarfélag': { member: 1, employer: 1.55 },
  'FIT — Félag iðn- og tæknigreina': { member: 0.8, employer: 1.75 },
};

export const AGREEMENTS: WageAgreement[] = [
  { id: 'sgs-bygg', name: 'SGS / Efling — Kjarasamningur bygginga 2024–2028', yv1Pct: 1.0385, yv2Pct: 1.169 },
  { id: 'efling-alm', name: 'Efling — Almennur kjarasamningur 2024–2028', yv1Pct: 1.0385, yv2Pct: 1.169 },
  { id: 'vr', name: 'VR — Verslunar- og skrifstofufólk 2024–2028', yv1Pct: 1.0173, yv2Pct: 1.169 },
  { id: 'samidn', name: 'Samiðn / FIT — Iðn- og tæknigreinar 2024–2028', yv1Pct: 1.0385, yv2Pct: 1.229 },
];

/** Fund lists are placeholders from public knowledge — replace with an authoritative source. */
export const FUNDS = {
  unions: [
    'Efling', 'VR', 'Eining-Iðja', 'AFL Starfsgreinafélag',
    'Verkalýðsfélag Akraness', 'Verkalýðsfélagið Hlíf', 'Framsýn stéttarfélag',
    'FIT — Félag iðn- og tæknigreina',
  ],
  pension: [
    'Gildi', 'Birta', 'Lífeyrissjóður verzlunarmanna', 'Festa', 'Stapi',
    'Brú', 'Frjálsi', 'Almenni', 'LSR', 'Lífsverk',
  ],
  sereign: [
    'Gildi — séreign', 'Birta — séreign', 'LIVE — séreign',
    'Frjálsi lífeyrissjóðurinn', 'Almenni lífeyrissjóðurinn', 'Allianz Ísland',
    'Arion — Lífeyrisauki', 'Landsbankinn — séreign',
  ],
};

/** Dagvinnustundir í mánuði — the divisor turning a monthly wage into an hourly rate. */
export const MONTHLY_HOURS = 173.33;

export const DEFAULT_SETTINGS: CompanySettings = {
  agreementId: 'sgs-bygg',
  periodType: 'month',
  dailyThreshold: 8,
  weeklyThreshold: 40,
  maxShiftHours: 16,
  yv1Pct: 1.0385,
  yv2Pct: 1.169,
  orlofPct: 10.17,
  orlofT2Years: 5,
  orlofT2Pct: 10.64,
  orlofT3Years: 10,
  orlofT3Pct: 12.07,
  pensionEmpPct: 4,
  pensionErPct: 11.5,
  matchExtraPct: 2,
  unionPct: 1,
  employerFundsPct: 1.55,
  tryggPct: 6.35,
  holidays: HOLIDAYS_2026,
  today: '2026-08-06',
};
